import type { ApiErrorResponse, LoginResponse, PaginationMeta } from '@school-erp/types';

const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The access token lives only in memory (never localStorage), so XSS cannot exfiltrate a
 * long-lived credential. The refresh token is an httpOnly cookie the browser sends by itself.
 */
let accessToken: string | null = null;
let refreshInFlight: Promise<LoginResponse | null> | null = null;
let onSessionExpired: (() => void) | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const setSessionExpiredHandler = (handler: (() => void) | null) => {
  onSessionExpired = handler;
};

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  /** Skip the bearer token and the refresh-and-retry behaviour (login, forgot-password, …). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '')
      url.searchParams.set(key, String(value));
  }
  return url.pathname + url.search;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = options.body instanceof FormData;
  // A form carries its own multipart boundary: the browser must be the one to write that header.
  if (options.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (!options.anonymous && accessToken) headers.Authorization = `Bearer ${accessToken}`;
  return fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body:
      options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
    credentials: 'same-origin',
  });
}

/** Exchanges the refresh cookie for a new access token. Concurrent callers share one request. */
export function refreshSession(): Promise<LoginResponse | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await send('/auth/refresh', { method: 'POST', anonymous: true });
      if (!response.ok) {
        accessToken = null;
        return null;
      }
      const body = (await response.json()) as { data: LoginResponse };
      accessToken = body.data.accessToken;
      return body.data;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  let response = await send(path, options);
  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await send(path, options);
    } else {
      onSessionExpired?.();
    }
  }
  if (!response.ok) throw await toApiError(response);
  return response;
}

async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ApiErrorResponse | null;
  return new ApiError(
    response.status,
    body?.error?.code ?? 'HTTP_ERROR',
    body?.error?.message ?? `Request failed (${response.status})`,
    body?.error?.details,
  );
}

export async function api<T>(path: string, options?: RequestOptions): Promise<T> {
  const response = await request(path, options);
  if (response.status === 204) return undefined as T;
  return ((await response.json()) as { data: T }).data;
}

/** Fetches a text body (e.g. a CSV export) with the same auth/refresh handling. */
export async function apiText(path: string, options?: RequestOptions): Promise<string> {
  const response = await request(path, options);
  return response.text();
}

/** Fetches a binary body (e.g. a PDF) with the same auth/refresh handling. */
export async function apiBlob(path: string, options?: RequestOptions): Promise<Blob> {
  const response = await request(path, options);
  return response.blob();
}

/** Saves a blob through a temporary link; used for PDF and CSV downloads. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function apiList<T>(
  path: string,
  options?: RequestOptions,
): Promise<{ data: T[]; meta: PaginationMeta }> {
  const response = await request(path, options);
  return (await response.json()) as { data: T[]; meta: PaginationMeta };
}
